import type { AlertFactory, AlertRetryWrapperFactory } from '@/features/shell'
import { type Member } from '@/features/iam/members/Member'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import {
  LegendVersionId,
  type PublishedLegend,
} from '@/features/jobs/legends/PublishedLegend'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import {
  EMPTY,
  combineLatest,
  merge,
  type Observable,
  of,
  Subject,
  firstValueFrom,
} from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  share,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { fromStream, toStream } from '@/utils/mobx-util'
import {
  fromAbortSignal,
  shareLatest,
  whileActive,
  withAbortSignal,
} from '@/utils/rx-util'
import { type Job } from '@/features/jobs/Job'
import { type LegendSelected } from './DraftPersister'
import { type DraftStateSlice, type PrepareContext } from './DraftJobViewModel'
import { type StepFactory, type StepViewModel } from './OutlineViewModel'
import { type PublishedLegendStore } from '@/features/jobs/legends/PublishedLegendStore'

const TITLE = 'How can we help you?'

/**
 * Legend change.
 */
type LegendChange = [legend: PublishedLegend | null, skipFetch: boolean]

/**
 * Legend Selector view model.
 */
export class LegendSelectorViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Whether `prepare` has completed since activation.
   */
  @observable private accessor _isReady = false

  private readonly _isReady$ = toStream(() => this._isReady, true)

  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Used to emit changes so we know when to trigger a save.
   *
   * @private
   */
  private readonly _changesSubject = new Subject<LegendChange>()

  /**
   * The selected Legend version.
   */
  @observable private accessor _selectedLegendVersion: PublishedLegend | null =
    null

  /**
   * Search text.
   */
  @observable accessor searchText = ''

  /**
   * Emits a value when the selected Legend version was changed by the user.
   */
  readonly changes$ = this._changesSubject.asObservable().pipe(
    // Ensure we have loaded the Legend entirely before
    // we broadcast the change.
    switchMap(([legend, skipFetch]) =>
      skipFetch
        ? of(legend)
        : withAbortSignal(async (signal) => {
            await this._createAlertRetryWrapper(
              'Unable to load the request type',
              signal,
            )(() => this._ensureLegendIsFullyLoaded(legend, signal))
            return legend
          }).pipe(catchError(() => EMPTY)),
    ),
    map((legend): LegendSelected => ({ type: 'LegendSelected', legend })),
    share(),
  )

  /**
   * Stream for the selected Legend version.
   * Starts emitting after `prepare` is done.
   *
   * TL;DR: Prevents issues around initial queries in other slices that
   * depend on the selected Legend version.
   *
   * This is so slices that depend on the selected Legend
   * won't receive a default of `null` immediately, but they still
   * want to react to setting `null` explicitly.
   *
   * For example, we want the Provider Selection slice
   * to wait with its' initial query until we have the selected
   * Legend fully loaded; there's a chance the Legend will be cleared
   * if it's inactive, in which case it will emit `null`.
   *
   * If we emitted immediately, then the `prepare` step of the
   * Provider Selection slice would see it as "oh we have no Legend, so
   * nothing to load" and the screen would render, then load in the background.
   * THAT'S NOT what we want; instead we want to prevent the render
   * until we have the ACTUAL state of the provider selection, which we can't
   * get until we know the final selected Legend.
   */
  readonly selectedLegendVersion$ = merge(
    // Emit the initial selection, but after that
    // use the changes stream since that will also ensure the legend
    // gets fetched before emitting.
    // The reason we do this is that a fully loaded legend contains
    // more info that other slices depend on, and we don't want them to
    // observe a partially loaded legend.
    toStream(() => this.selectedLegendVersion, true).pipe(take(1)),
    this.changes$.pipe(map((x) => x.legend)),
  ).pipe(
    distinctUntilChanged(),
    shareLatest(),
    // Don't start emitting until the slice is ready.
    whileActive(this._isReady$),
  )

  /**
   * Streams as Observables.
   *
   * @private
   */
  private readonly _$

  /**
   * The scroll provider for Legends.
   */
  readonly scroller = new InfiniteScrollProvider<PublishedLegend>({
    limit: 20,
    fetchItems: async ({ paging, signal }) => {
      const workspace = this._$.selectedWorkspaceMember()?.workspace
      return workspace
        ? this._createAlertRetryWrapper(
            'Loading request types failed',
            signal,
          )(() => this._listLegends(workspace, paging, signal))
        : CursorPaged.empty<PublishedLegend>()
    },
  })

  /**
   * Constructor.
   */
  constructor(
    createStep: StepFactory,
    private readonly _selectedWorkspaceMember$: Observable<Member | null>,
    private readonly _publishedLegendStore: PublishedLegendStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _isDisabled: () => boolean,
  ) {
    this._$ = {
      selectedWorkspaceMember: fromStream(null, this._selectedWorkspaceMember$),
    }

    this._lifecycle.manage(this.selectedLegendVersion$)
    this._lifecycle.manage(this.changes$)
    // If we haven't selected a Legend yet, whenever the workspace changes we
    // want to immediately fetch the first page of Legends. We do this because
    // we know that the user needs to select a Legend.
    this._lifecycle.manage(
      combineLatest([
        toStream(() => this._$.selectedWorkspaceMember(), true),
        toStream(() => this.selectedLegendVersion, true),
      ]).pipe(
        filter(([, legend]) => legend === null),
        switchMap(() =>
          withAbortSignal((signal) => this.scroller.fetch({ signal })).pipe(
            catchError(() => EMPTY),
          ),
        ),
        whileActive(this._isReady$),
      ),
    )
    this.step = createStep({
      stepId: 'legend',
      isCompleted: () => this.selectedLegendVersion !== null,
      isShortCircuited: () => this.selectedLegendVersion === null,
      outlineItemDisplay: () => {
        if (this.selectedLegendVersion === null) {
          return { title: TITLE }
        }

        return {
          title: TITLE,
          description: null,
          value: this.selectedLegendVersion.name,
          visual: {
            type: 'Image',
            url: this.selectedLegendVersion.imageUrl,
          },
        }
      },
    })
  }

  /**
   * This is the only step accessor.
   * We can only select a Legend when we have a Workspace.
   */
  @computed
  get stepAccessors() {
    return this._$.selectedWorkspaceMember() ? [this] : []
  }

  /**
   * Whether the Legend Editor is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * The currently selected legend version, or `null` if not currently valid.
   */
  @computed
  get selectedLegendVersion() {
    if (!this._selectedLegendVersion) {
      return null
    }

    if (this.legends.includes(this._selectedLegendVersion)) {
      return this._selectedLegendVersion
    }

    return null
  }

  /**
   * Available Legends.
   */
  @computed
  get legends() {
    const workspace = this._$.selectedWorkspaceMember()?.workspace
    if (!workspace) {
      return []
    }

    return this._publishedLegendStore.forWorkspace(workspace.id)
  }

  /**
   * We do text filtering on the client.
   */
  @computed
  get filteredLegends() {
    const lower = this.searchText.toLowerCase()
    const legends = this.legends
    return legends.filter((l) => l.name.toLowerCase().includes(lower))
  }

  /**
   * Whether we are currently fetching Legend details.
   */
  @computed
  get isFetchingLegendDetails() {
    return this._ensureLegendIsFullyLoaded.pending
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    this._isReady = false
    this._lifecycle.deactivate()
  }

  /** @inheritDoc */
  @action.bound
  ready() {
    this._isReady = this._lifecycle.active
  }

  /**
   * Hydrates the state from the Job.
   *
   * @param context
   * @param signal
   */
  async prepare(context: PrepareContext, signal?: AbortSignal) {
    // Wait for the WS selector to have been prepared.
    await firstValueFrom(
      this._selectedWorkspaceMember$.pipe(takeUntil(fromAbortSignal(signal))),
      { defaultValue: null },
    )

    signal?.throwIfAborted()
    if (context.type === 'EditDraft') {
      // Load existing Legend for Job.
      const job = context.job
      await this._fetchLegendFromExistingDraft(job, signal)
    } else if (context.prefilledDraft.legendId) {
      await this._preselectLegendForNewDraft(
        context.prefilledDraft.legendId,
        signal,
      )
    }
  }

  /**
   * Selects the legend and emits a change notification.
   */
  @action.bound
  selectLegend(legend: PublishedLegend | undefined | null, skipFetch = false) {
    legend ??= null
    if (legend !== this._selectedLegendVersion) {
      this._setLegend(legend)
      this._changesSubject.next([legend, skipFetch])
    }
  }

  /**
   * Sets the search text.
   *
   * @param newText
   */
  @action.bound
  setSearchText(newText: string) {
    this.searchText = newText
  }

  /**
   * Lists legends for the specified Workspace.
   *
   * @private
   */
  private _listLegends(
    workspace: Workspace,
    paging: CursorPageQuery,
    signal?: AbortSignal,
  ) {
    return this._publishedLegendStore.listLegends(
      { ...paging, workspaceId: workspace.id },
      signal,
    )
  }

  /**
   * Ensure that the Legend is fully loaded.
   */
  private _ensureLegendIsFullyLoaded = task.resolved(
    async (legend: PublishedLegend | null, signal: AbortSignal) => {
      if (legend === null) {
        return
      }
      await this._publishedLegendStore.fetchLegendVersion(
        legend.versionId,
        signal,
      )
    },
  )

  /**
   * Sets the legend.
   *
   * @param legend
   * @private
   */
  @action.bound
  private _setLegend(legend: PublishedLegend | null) {
    this._selectedLegendVersion = legend
  }

  /**
   * Loads the Legend for an existing draft.
   * If the Legend is now inactive, clears the selection.
   * If a new Legend Version has been deployed, it is updated to the
   * latest version.
   *
   * @param job
   * @param signal
   * @private
   */
  private async _fetchLegendFromExistingDraft(job: Job, signal?: AbortSignal) {
    const latest = await this._publishedLegendStore.fetchLatestLegendVersion(
      job.legendVersionId.id,
      signal,
    )
    signal?.throwIfAborted()
    if (latest.inactive) {
      // The Legend is inactive, so clear the selection.
      this.selectLegend(null)
      this._informAboutInactiveLegend()
    } else if (
      !LegendVersionId.equals(job.legendVersionId, latest.versionId) &&
      job.isDraft
    ) {
      // Select it now, triggers a change, and inform about the upgrade.
      // Since we just fetched the latest version, we can skip fetching.
      this.selectLegend(latest, true)
      this._informAboutUpgrade()
    } else {
      this._setLegend(latest)
    }
  }

  /**
   * Preselects a Legend for a new draft.
   *
   * @param legendId
   * @param signal
   * @private
   */
  private async _preselectLegendForNewDraft(
    legendId: string,
    signal?: AbortSignal,
  ) {
    // New draft with preselected Legend.
    const preselected =
      await this._publishedLegendStore.fetchLatestLegendVersion(
        legendId,
        signal,
      )
    signal?.throwIfAborted()
    if (!preselected.inactive) {
      this.selectLegend(
        preselected,
        // Skip fetching before emitting the change since we just did.
        true,
      )
    }
  }

  /**
   * Informs the user about an inactive Legend.
   *
   * @private
   */
  private _informAboutInactiveLegend() {
    this._newAlert()
      .saying(
        'Attention',
        'This type of request is no longer available. Please select a new one.',
      )
      .withState('warning')
      .show()
      .autoDismiss(60_000)
  }

  /**
   * Informs the user about the Draft having been updated.
   * @private
   */
  private _informAboutUpgrade() {
    this._newAlert()
      .saying(
        'Notice',
        `This draft has been updated to a newer version. You don't have to do anything else.`,
      )
      .withState('neutral')
      .show()
      .autoDismiss(60_000)
  }
}

/**
 * Factory for creating a {@link LegendSelectorViewModel}.
 */
export type LegendSelectorViewModelFactory = ReturnType<
  typeof createLegendSelectorViewModelFactory
>

/** @see LegendSelectorViewModelFactory */
export function createLegendSelectorViewModelFactory(
  publishedLegendStore: PublishedLegendStore,
  newAlert: AlertFactory,
  createAlertRetryWrapper: AlertRetryWrapperFactory,
) {
  return (
    createStep: StepFactory,
    selectedWorkspaceMember$: Observable<Member | null>,
    isDisabled: () => boolean,
  ) =>
    new LegendSelectorViewModel(
      createStep,
      selectedWorkspaceMember$,
      publishedLegendStore,
      newAlert,
      createAlertRetryWrapper,
      isDisabled,
    )
}
